import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Layout from "../../../components/Layout/Layout";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import QuickOnlineAccountServices from "../../../services/QuickOnlineAccountServices";
import { HollowDotsSpinner } from "react-epic-spinners";
import {
  ArrowLeftIcon,
  PencilIcon,
  BanIcon,
} from "@heroicons/react/outline";
import { convert } from "../../../utils/convertDate";
import fire from "../../../utils/Toastr";

function DetailRow({ label, value, fullWidth = false }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">
        {value || "N/A"}
      </dd>
    </div>
  );
}

export default function ViewQuickOnlineReferralCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const code = searchParams.get("code");
  const stateRecord = location.state?.record;
  const stateActiveFilter = location.state?.isActiveFilter;

  const [record, setRecord] = useState(stateRecord || null);
  const [isActive, setIsActive] = useState(
    typeof stateRecord?.isActive === "boolean"
      ? stateRecord.isActive
      : stateActiveFilter === "true"
        ? true
        : null
  );
  const [fetchLoading, setFetchLoading] = useState(!stateRecord);
  const [deactivating, setDeactivating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!id) {
      setFetchLoading(false);
      fire("error", "Referral code ID is missing");
      navigate("/online/accounts/referral-codes");
      return;
    }

    if (stateRecord && typeof stateRecord.isActive === "boolean") {
      setRecord(stateRecord);
      setIsActive(stateRecord.isActive);
      setFetchLoading(false);
      return;
    }

    if (stateRecord && stateActiveFilter === "true") {
      setRecord(stateRecord);
      setIsActive(true);
      setFetchLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        const term = code || stateRecord?.code || "";
        const activeResp = await QuickOnlineAccountServices.listReferralCodes(
          1,
          50,
          term,
          "true"
        );
        const activeMatch = activeResp?.data?.result?.find(
          (item) => String(item.id) === String(id)
        );

        if (activeMatch) {
          setRecord(activeMatch);
          setIsActive(true);
          return;
        }

        if (stateRecord) {
          setRecord(stateRecord);
          setIsActive(false);
          return;
        }

        const allResp = await QuickOnlineAccountServices.listReferralCodes(
          1,
          50,
          term,
          ""
        );
        const match = allResp?.data?.result?.find(
          (item) => String(item.id) === String(id)
        );

        if (match) {
          setRecord(match);
          setIsActive(
            typeof match.isActive === "boolean" ? match.isActive : false
          );
        } else {
          fire(
            "error",
            allResp?.message || "Failed to fetch referral code details"
          );
        }
      } catch (error) {
        console.error(
          "Error fetching quick online referral code details:",
          error
        );
        fire("error", "Failed to fetch referral code details");
      } finally {
        setFetchLoading(false);
      }
    };

    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDeactivate = async () => {
    if (!record?.id) return;
    setDeactivating(true);
    try {
      const resp = await QuickOnlineAccountServices.deleteReferralCode(
        record.id
      );
      if (resp && resp.status !== false) {
        fire("success", resp.message || "Referral code deactivated successfully");
        navigate("/online/accounts/referral-codes");
      } else {
        fire("error", resp?.message || "Failed to deactivate referral code");
      }
    } catch (error) {
      console.error("Error deactivating quick online referral code:", error);
      fire("error", "Failed to deactivate referral code");
    } finally {
      setDeactivating(false);
      setConfirmOpen(false);
    }
  };

  const openEdit = () => {
    if (!record) return;
    navigate(
      `/online/accounts/referral-codes/edit?id=${encodeURIComponent(
        record.id
      )}&code=${encodeURIComponent(record.code || "")}`,
      {
        state: {
          record: { ...record, isActive },
          isActive: String(isActive),
        },
      }
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return convert(dateStr);
  };

  return (
    <Layout
      title="View Quick Online Referral Code"
      pageTag="account"
      current="/online/accounts/referral-codes"
    >
      <React.Fragment>
        <main>
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Link
                to="/online/accounts/referral-codes"
                className="inline-flex items-center text-sm text-red-600 hover:text-red-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to Quick Online Referral Codes
              </Link>
            </div>

            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-medium text-gray-900">
                    Referral Code Details
                  </h2>
                  {isActive === true && (
                    <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                      Active
                    </span>
                  )}
                  {isActive === false && (
                    <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
                      Inactive
                    </span>
                  )}
                </div>
                {record && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={openEdit}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PencilIcon className="w-4 h-4 mr-1.5" />
                      Edit
                    </button>
                    {isActive !== false && (
                      <button
                        type="button"
                        onClick={() => setConfirmOpen(true)}
                        disabled={deactivating}
                        className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
                      >
                        <BanIcon className="w-4 h-4 mr-1.5" />
                        Deactivate
                      </button>
                    )}
                  </div>
                )}
              </div>

              {fetchLoading ? (
                <div className="flex justify-center py-12">
                  <HollowDotsSpinner size={50} color="#4D627E" />
                </div>
              ) : !record ? (
                <div className="text-center py-12 text-gray-500">
                  Referral code details not found
                </div>
              ) : (
                <div className="px-6 py-6">
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    <DetailRow label="Referral Code" value={record.code} />
                    <DetailRow label="MIS Code" value={record.misCode} />
                    <DetailRow label="First Name" value={record.firstName} />
                    <DetailRow label="Last Name" value={record.lastName} />
                    <DetailRow
                      label="Email Address"
                      value={record.emailAddress}
                    />
                    <DetailRow
                      label="Created On"
                      value={formatDateTime(record.createdOn)}
                    />
                    <DetailRow label="ID" value={record.id} />
                    <DetailRow
                      label="Status"
                      value={
                        isActive === true
                          ? "Active"
                          : isActive === false
                            ? "Inactive"
                            : "Unknown"
                      }
                    />
                    <DetailRow
                      label="Description"
                      value={record.description}
                      fullWidth
                    />
                  </dl>
                </div>
              )}
            </div>
          </div>
        </main>
      </React.Fragment>

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeactivate}
        title="Deactivate Referral Code"
        message={`Are you sure you want to deactivate referral code "${
          record?.code || ""
        }"? It will no longer be available for new account openings.`}
        confirmText="Deactivate"
        cancelText="Cancel"
        isLoading={deactivating}
      />
    </Layout>
  );
}
